import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface State {
  url: string | null;
  loading: boolean;
  error: unknown;
}

/**
 * Fetches an image from an authenticated API path, converts to an object URL
 * suitable for `<img src>`. The URL is revoked when the path changes or the
 * component unmounts, so callers don't need to manage cleanup.
 */
export function useAuthedImage(path: string | null | undefined): State {
  const [state, setState] = useState<State>({ url: null, loading: !!path, error: null });

  useEffect(() => {
    if (!path) {
      setState({ url: null, loading: false, error: null });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setState({ url: null, loading: true, error: null });

    apiClient
      .get<Blob>(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setState({ url: objectUrl, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ url: null, loading: false, error: err });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return state;
}
